import type { ReactNode } from 'react';
import { forwardRef } from 'react';

import type { A11y, Classnames, IconList } from '@snowit/icetypes';

import testId from '@snowit/utils/testId';

import Icon from '../../../Icon';
import Loader from '../../../Loaders/LoaderSpinner';
import Markdown from '../../../Typography/Markdown';
import Typo from '../../../Typography/Typo';

import cx from 'classnames';
import s from './DropdownItem.module.scss';

export interface IDdItem {
  label?: ReactNode;
  color?: string;
  hint?: string | string[];
  tabIndex?: number;
  className?: string;
  classNames?: Classnames<'item' | 'icon' | 'wrapper' | 'label' | 'hint'>;
  a11y?: A11y;
  /** Jest/Cypress test string */
  test?: string;
}

export interface IDropdownItem extends IDdItem {
  icon?: IconList;
  left?: ReactNode;
  right?: ReactNode;
  bottom?: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  onClick?: (...args: any[]) => void;
  onKeyDown?: (...args: any[]) => void;
}

const DropdownItem = forwardRef<HTMLDivElement, IDropdownItem>((props, ref) => {
  const {
    icon,
    label,
    color,
    left,
    right,
    bottom,
    disabled,
    loading,
    onClick,
    onKeyDown,
    className,
    classNames,
    a11y,
    test,
  } = props;

  const renderLoading = () => {
    if (!loading) return null;
    return <Loader />;
  };

  const renderIcon = () => {
    if (loading) return null;
    if (!icon) return null;
    return <Icon className={cx(s.icon, classNames?.icon)} name={icon} color={color ?? 'text'} />;
  };

  const renderLabel = () => {
    if (!label) return null;
    if (typeof label === 'string') {
      return (
        <Typo
          className={classNames?.label}
          text={label}
          tag={'p'}
          size={'df'}
          color={color ?? 'text'}
        />
      );
    }
    return label;
  };

  const renderHint = () => {
    if (!props.hint) return null;
    const hints: string[] = (Array.isArray(props.hint) ? props.hint : [props.hint]).filter(Boolean);
    if (!hints.length) return null;
    return hints.map((hint, i) => (
      <Markdown
        key={hint || i}
        classNames={{ text: classNames?.hint }}
        source={hint}
        baseSize={'sm'}
      />
    ));
  };

  return (
    <div
      ref={ref}
      className={cx(s.item, className, classNames?.item, { [s.disabled]: disabled })}
      role={a11y?.role}
      tabIndex={disabled ? -1 : a11y?.tabIndex}
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-disabled={disabled}
      {...testId(test)}
    >
      {renderLoading()}
      {renderIcon()}
      {!loading && !icon && left}
      <div className={cx(s.wrapper, classNames?.wrapper)}>
        {renderLabel()}
        {renderHint()}
        {bottom}
      </div>
      {right}
    </div>
  );
});

export default DropdownItem;
